import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import moment from 'moment';

import DateRangeSelector from '../../../components/DateRangeSelector';
import { retrieveStaffUsers } from '../../tasks/services/TaskCRUD';
import { TaskUserModel } from '../../tasks/models/TaskUserModel';
import { SUBSCRIPTION_GROUP_ID } from '../../tasks/redux/TaskSaga';
import {
  getCrossSellingReport,
  CrossSellingReportRow,
} from '../services/CrossSellingCRUD';

export default function CrossSellingReport() {
  const intl = useIntl();
  const [since, setSince] = useState<Date>();
  const [until, setUntil] = useState<Date>();
  const [agentId, setAgentId] = useState<string>('');
  const [agents, setAgents] = useState<TaskUserModel[]>([]);
  const [rows, setRows] = useState<CrossSellingReportRow[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  async function fetchAgents() {
    try {
      const { data } = await retrieveStaffUsers(SUBSCRIPTION_GROUP_ID);
      setAgents(data);
    } catch (error) {
      console.warn(error);
    }
  }

  useEffect(() => {
    fetchAgents();
  }, []);

  async function onGenerateReport() {
    if (!since || !until) {
      toast.error(
        intl.formatMessage({ id: 'crossSellingReport.dateRangeRequired' })
      );
      return;
    }

    try {
      setLoading(true);
      const { data } = await getCrossSellingReport({
        since,
        until,
        agent: agentId ? Number(agentId) : undefined,
      });
      setRows(data);
    } catch (error) {
      console.warn(error);
      toast.error(intl.formatMessage({ id: 'crossSellingReport.fetchError' }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h1 className="mb-8">
        {intl.formatMessage({ id: 'crossSellingReport.title' })}
      </h1>
      <div className="d-flex gap-4 align-items-end">
        <DateRangeSelector
          initialSince={since}
          initialUntil={until}
          onSelected={(selectedSince, selectedUntil) => {
            setSince(selectedSince);
            setUntil(selectedUntil);
          }}
        />
        <div className="form-group">
          <label htmlFor="agent" className="form-label">
            {intl.formatMessage({ id: 'crossSellingReport.agentLabel' })}
          </label>
          <select
            id="agent"
            className="form-select"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            <option value="">
              {intl.formatMessage({ id: 'crossSellingReport.allAgents' })}
            </option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.email}
              </option>
            ))}
          </select>
        </div>
        <button
          className="btn btn-primary"
          onClick={onGenerateReport}
          disabled={loading || !since || !until}
        >
          {loading && (
            <span className="spinner-border spinner-border-sm me-2" />
          )}
          {intl.formatMessage({ id: 'crossSellingReport.generate' })}
        </button>
      </div>

      {rows !== null &&
        (rows.length === 0 ? (
          <p className="text-muted mt-8">
            {intl.formatMessage({ id: 'crossSellingReport.empty' })}
          </p>
        ) : (
          <div className="table-responsive mt-8">
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>
                    {intl.formatMessage({ id: 'crossSellingReport.date' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'crossSellingReport.order' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'crossSellingReport.agent' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'crossSellingReport.product' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'crossSellingReport.units' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'crossSellingReport.value' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{moment(row.date).format('DD/MM/YYYY HH:mm')}</td>
                    <td>{row.order_code}</td>
                    <td>{row.agent}</td>
                    <td>{row.product}</td>
                    <td>{row.units}</td>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}
